/**
 * Cohort acquisition & activation analysis — ported from data-crunch/analyze-api.py
 *
 * Merges 4 CSV sources (contacts, contacts-pro, clients, tests) to produce:
 * - Acquisition cohorts by month/quarter, with/without pros
 * - Yearly recap (contacts acquired vs sales)
 * - Activation cohorts (persons who did a trial)
 */

const EXCLUDED_GROUPS = new Set(["Wyes Internal Affairs"]);
const DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const MIN_COHORT_MONTH = '2023-07';

function isDateLike(s) {
  return s && DATE_RE.test(s);
}

// --- Helpers ---

function monthToQuarter(ym) {
  const [y, m] = ym.split("-");
  const q = Math.floor((parseInt(m) - 1) / 3) + 1;
  return `${y}-Q${q}`;
}

function fillMonths(keys) {
  if (keys.length === 0) return [];
  const sorted = [...keys].sort();
  const [minY, minM] = sorted[0].split("-").map(Number);
  const [maxY, maxM] = sorted[sorted.length - 1].split("-").map(Number);
  const result = [];
  let y = minY,
    m = minM;
  while (y < maxY || (y === maxY && m <= maxM)) {
    result.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return result;
}

function fillQuarters(keys) {
  if (keys.length === 0) return [];
  const sorted = [...keys].sort();
  let [y, q] = [parseInt(sorted[0].slice(0, 4)), parseInt(sorted[0].slice(-1))];
  const [maxY, maxQ] = [
    parseInt(sorted[sorted.length - 1].slice(0, 4)),
    parseInt(sorted[sorted.length - 1].slice(-1)),
  ];
  const result = [];
  while (y < maxY || (y === maxY && q <= maxQ)) {
    result.push(`${y}-Q${q}`);
    q++;
    if (q > 4) {
      q = 1;
      y++;
    }
  }
  return result;
}

function counter(items) {
  const c = {};
  for (const item of items) {
    c[item] = (c[item] || 0) + 1;
  }
  return c;
}

function aggregateQuarters(monthlyCounts) {
  const q = {};
  for (const [ym, count] of Object.entries(monthlyCounts)) {
    const qk = monthToQuarter(ym);
    q[qk] = (q[qk] || 0) + count;
  }
  return q;
}

function sumValues(obj) {
  return Object.values(obj).reduce((a, b) => a + b, 0);
}

function filterMonthCohorts(counts, minMonth) {
  const filtered = {};
  for (const [key, val] of Object.entries(counts)) {
    if (key >= minMonth) filtered[key] = val;
  }
  return filtered;
}

// --- Main analysis ---

export function analyzeCohorts({ contactsRows, contactsProRows, clientsRows, testsRows }) {
  // --- Load all sources ---

  const particulierDates = {};
  let particulierNoDate = 0;

  for (const row of contactsRows || []) {
    const nameOrig = (row["Nom"] || "").trim();
    const name = nameOrig.toLowerCase();
    if (!name) continue;
    const dateStr = (row["Date de premier contact"] || "").trim();
    if (isDateLike(dateStr)) {
      particulierDates[name] = dateStr;
    } else {
      particulierNoDate++;
    }
  }

  const proDates = {};
  let proNoDate = 0;

  for (const row of contactsProRows || []) {
    const nameOrig = (row["Nom"] || "").trim();
    const name = nameOrig.toLowerCase();
    if (!name) continue;
    const dateStr = (row["Date de premier contact"] || "").trim();
    if (name in particulierDates) continue;
    if (isDateLike(dateStr)) {
      proDates[name] = dateStr;
    } else {
      proNoDate++;
    }
  }

  const contactDates = { ...particulierDates, ...proDates };
  const contactNoDate = particulierNoDate + proNoDate;

  const clientDates = {};
  const clientNamesSet = new Set();
  const clientInstallDates = {};
  let clientNoDate = 0;

  for (const row of clientsRows || []) {
    if (EXCLUDED_GROUPS.has((row["Groupe"] || "").trim())) continue;
    const nameOrig = (row["Nom"] || "").trim();
    const name = nameOrig.toLowerCase();
    if (!name) continue;
    clientNamesSet.add(name);
    const install = (row["Date d'installation"] || "").trim();
    if (isDateLike(install)) clientInstallDates[name] = install;

    let dateStr = (row["Date de premier contact"] || "").trim();
    if (!isDateLike(dateStr)) dateStr = isDateLike(install) ? install : "";
    if (!dateStr) { const d = (row["Date de dernier contact"] || "").trim(); if (isDateLike(d)) dateStr = d; }
    if (name in contactDates) continue;
    if (dateStr) {
      clientDates[name] = dateStr;
    } else {
      clientNoDate++;
    }
  }

  const testNames = new Set();
  for (const row of testsRows || []) {
    const name = (row["Nom"] || "").trim().toLowerCase();
    if (name) testNames.add(name);
  }

  // --- Monthly cohorts (filtered to MIN_COHORT_MONTH) ---

  const particulierCohortsM = filterMonthCohorts(counter(Object.values(particulierDates).map((d) => d.slice(0, 7))), MIN_COHORT_MONTH);
  const proCohortsM = filterMonthCohorts(counter(Object.values(proDates).map((d) => d.slice(0, 7))), MIN_COHORT_MONTH);
  const clientCohortsM = filterMonthCohorts(counter(Object.values(clientDates).map((d) => d.slice(0, 7))), MIN_COHORT_MONTH);

  // With pros
  const mergedDatesAll = { ...clientDates, ...contactDates };
  const acqCohortsM = {};
  const actCohortsM = {};
  for (const [name, date] of Object.entries(mergedDatesAll)) {
    const cohort = date.slice(0, 7);
    if (cohort < MIN_COHORT_MONTH) continue;
    acqCohortsM[cohort] = (acqCohortsM[cohort] || 0) + 1;
    if (testNames.has(name)) {
      actCohortsM[cohort] = (actCohortsM[cohort] || 0) + 1;
    }
  }

  // Without pros
  const mergedDatesNopro = { ...clientDates, ...particulierDates };
  const acqCohortsMNp = {};
  const actCohortsMNp = {};
  for (const [name, date] of Object.entries(mergedDatesNopro)) {
    const cohort = date.slice(0, 7);
    if (cohort < MIN_COHORT_MONTH) continue;
    acqCohortsMNp[cohort] = (acqCohortsMNp[cohort] || 0) + 1;
    if (testNames.has(name)) {
      actCohortsMNp[cohort] = (actCohortsMNp[cohort] || 0) + 1;
    }
  }

  // --- Quarterly cohorts ---

  const particulierCohortsQ = aggregateQuarters(particulierCohortsM);
  const proCohortsQ = aggregateQuarters(proCohortsM);
  const clientCohortsQ = aggregateQuarters(clientCohortsM);
  const acqCohortsQ = aggregateQuarters(acqCohortsM);
  const actCohortsQ = aggregateQuarters(actCohortsM);
  const acqCohortsQNp = aggregateQuarters(acqCohortsMNp);
  const actCohortsQNp = aggregateQuarters(actCohortsMNp);

  // --- Fill periods ---

  const allMonths = fillMonths(Object.keys(acqCohortsM));
  const allQuarters = fillQuarters(Object.keys(acqCohortsQ));

  // --- Totals ---

  const contactTotal = sumValues(particulierCohortsM) + sumValues(proCohortsM);
  const clientTotal = sumValues(clientCohortsM);
  const acqTotal = contactTotal + clientTotal;
  const acqNoDate = contactNoDate + clientNoDate;
  const actTotal = testNames.size;
  const actWith = sumValues(actCohortsM);
  const actNo = actTotal - actWith;
  const actRate = acqTotal > 0 ? Math.round((actWith / acqTotal) * 100) : 0;

  const contactTotalNp = sumValues(particulierCohortsM);
  const acqTotalNp = contactTotalNp + clientTotal;
  const acqNoDateNp = particulierNoDate + clientNoDate;
  const actWithNp = sumValues(actCohortsMNp);
  const actNoNp = actTotal - actWithNp;
  const actRateNp = acqTotalNp > 0 ? Math.round((actWithNp / acqTotalNp) * 100) : 0;

  // --- Yearly recap ---

  const minYear = MIN_COHORT_MONTH.slice(0, 4);
  const contactsByYear = {};
  for (const date of Object.values(contactDates)) {
    if (date < MIN_COHORT_MONTH) continue;
    const y = date.slice(0, 4);
    contactsByYear[y] = (contactsByYear[y] || 0) + 1;
  }

  const salesByYear = {};
  let salesNoDate = 0;
  for (const row of clientsRows || []) {
    if (EXCLUDED_GROUPS.has((row["Groupe"] || "").trim())) continue;
    const d = (row["Date d'installation"] || "").trim();
    if (isDateLike(d)) {
      if (d < MIN_COHORT_MONTH) continue;
      const y = d.slice(0, 4);
      salesByYear[y] = (salesByYear[y] || 0) + 1;
    } else {
      salesNoDate++;
    }
  }

  const allYears = [...new Set([...Object.keys(contactsByYear), ...Object.keys(salesByYear)])].sort(
    (a, b) => b.localeCompare(a),
  );
  const yearlyRows = allYears.map((y) => {
    const co = contactsByYear[y] || 0;
    const s = salesByYear[y] || 0;
    const conv = co > 0 ? `${Math.round((s / co) * 100)}%` : "-";
    return { year: y, contacts: co, sales: s, conversion: conv };
  });
  const yearlyAcqTotal = sumValues(contactsByYear);
  const salesTotal = sumValues(salesByYear);
  const yearlyConvTotal = yearlyAcqTotal > 0 ? `${Math.round((salesTotal / yearlyAcqTotal) * 100)}%` : "-";

  // --- Build variant data ---

  function buildVariant(
    labels,
    particulierC,
    proC,
    clientC,
    acqC,
    actC,
    kpi,
  ) {
    return {
      labels,
      acqParticuliers: labels.map((p) => particulierC[p] || 0),
      acqPros: labels.map((p) => proC[p] || 0),
      acqClients: labels.map((p) => clientC[p] || 0),
      actAcquired: labels.map((p) => acqC[p] || 0),
      actActivated: labels.map((p) => actC[p] || 0),
      acqTable: buildAcqTable(labels, particulierC, proC, clientC),
      actTable: buildActTable(labels, acqC, actC),
      kpi,
    };
  }

  const kpiAll = {
    acqTotal,
    acqContacts: contactTotal,
    acqClients: clientTotal,
    acqNoDate,
    actTotal,
    actWith,
    actNo,
    actRate,
  };
  const kpiNopro = {
    acqTotal: acqTotalNp,
    acqContacts: contactTotalNp,
    acqClients: clientTotal,
    acqNoDate: acqNoDateNp,
    actTotal,
    actWith: actWithNp,
    actNo: actNoNp,
    actRate: actRateNp,
  };

  const emptyC = {};

  return {
    monthly: {
      all: buildVariant(
        allMonths,
        particulierCohortsM, proCohortsM, clientCohortsM,
        acqCohortsM, actCohortsM, kpiAll,
      ),
      nopro: buildVariant(
        allMonths,
        particulierCohortsM, emptyC, clientCohortsM,
        acqCohortsMNp, actCohortsMNp, kpiNopro,
      ),
    },
    quarterly: {
      all: buildVariant(
        allQuarters,
        particulierCohortsQ, proCohortsQ, clientCohortsQ,
        acqCohortsQ, actCohortsQ, kpiAll,
      ),
      nopro: buildVariant(
        allQuarters,
        particulierCohortsQ, emptyC, clientCohortsQ,
        acqCohortsQNp, actCohortsQNp, kpiNopro,
      ),
    },
    yearly: {
      rows: yearlyRows,
      acqTotal: yearlyAcqTotal,
      salesTotal,
      convTotal: yearlyConvTotal,
      noDateContacts: contactNoDate + clientNoDate,
      noDateSales: salesNoDate,
    },
  };
}

// --- Table builders ---

function buildAcqTable(labels, particulierC, proC, clientC) {
  return [...labels]
    .sort((a, b) => b.localeCompare(a))
    .map((p) => {
      const pa = particulierC[p] || 0;
      const pr = proC[p] || 0;
      const co = pa + pr;
      const cl = clientC[p] || 0;
      const total = co + cl;
      const pct = co > 0 ? Math.round((cl / co) * 100) : null;
      return { period: p, contacts: co, clients: cl, total, pct };
    })
    .filter((r) => r.total > 0);
}

function buildActTable(labels, acqC, actC) {
  return [...labels]
    .sort((a, b) => b.localeCompare(a))
    .map((p) => {
      const acq = acqC[p] || 0;
      const act = actC[p] || 0;
      const rate = acq > 0 ? `${Math.round((act / acq) * 100)}%` : "-";
      return { period: p, acquired: acq, activated: act, rate };
    })
    .filter((r) => r.acquired > 0);
}
